package at.theatervogelweide.admin.contract

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

/**
 * The one error type of the admin app. RPC calls return it inside [AdminResult.Failure],
 * HTTP endpoints respond with it as the body and with [ErrorCode.httpStatus] as the status.
 *
 * [message] is German and can be shown as is; [fieldErrors] belong to the form fields named by [FieldError.field].
 */
@Serializable
data class AdminError(
    val code: ErrorCode,
    val message: String,
    val fieldErrors: List<FieldError> = emptyList(),
) {
    companion object {
        fun validation(fieldErrors: List<FieldError>) =
            AdminError(ErrorCode.VALIDATION, "Bitte die markierten Felder korrigieren.", fieldErrors)

        fun unauthenticated() = AdminError(ErrorCode.UNAUTHENTICATED, "Bitte melde dich an.")

        fun forbidden() = AdminError(ErrorCode.FORBIDDEN, "Diese Anfrage ist nicht erlaubt.")

        fun notFound() = AdminError(ErrorCode.NOT_FOUND, "Nicht gefunden.")

        fun conflict() = AdminError(
            ErrorCode.CONFLICT,
            "Jemand anderes hat diesen Eintrag inzwischen geändert. Bitte neu laden.",
        )

        fun internal() = AdminError(ErrorCode.INTERNAL, "Etwas ist schiefgegangen. Bitte versuche es noch einmal.")

        /** Too many requests from one address in a short time, e.g. to the one-time links (#66). */
        fun tooManyRequests() = AdminError(
            ErrorCode.TOO_MANY_REQUESTS,
            "Zu viele Anfragen in kurzer Zeit. Bitte versuche es in einer Minute noch einmal.",
        )

        /** Too many failed sign-ins (#66); [minutes] until the next try, at least 1. */
        fun tooManyAttempts(minutes: Long) = AdminError(
            ErrorCode.TOO_MANY_REQUESTS,
            "Zu viele fehlgeschlagene Anmeldungen. Bitte versuche es in " +
                (if (minutes <= 1) "einer Minute" else "$minutes Minuten") + " noch einmal.",
        )
    }
}

@Serializable
data class FieldError(
    val field: String,
    val message: String,
)

@Serializable
enum class ErrorCode(val httpStatus: Int) {
    @SerialName("validation")
    VALIDATION(400),

    @SerialName("unauthenticated")
    UNAUTHENTICATED(401),

    @SerialName("forbidden")
    FORBIDDEN(403),

    @SerialName("not-found")
    NOT_FOUND(404),

    @SerialName("conflict")
    CONFLICT(409),

    @SerialName("too-many-requests")
    TOO_MANY_REQUESTS(429),

    @SerialName("internal")
    INTERNAL(500),
}

/** The result of every kotlinx.rpc call: either the value or an [AdminError]. */
@Serializable
sealed interface AdminResult<out T> {
    @Serializable
    @SerialName("success")
    data class Success<out T>(val value: T) : AdminResult<T>

    @Serializable
    @SerialName("failure")
    data class Failure(val error: AdminError) : AdminResult<Nothing>
}

inline fun <T, R> AdminResult<T>.map(transform: (T) -> R): AdminResult<R> = when (this) {
    is AdminResult.Success -> AdminResult.Success(transform(value))
    is AdminResult.Failure -> this
}

fun <T> AdminResult<T>.valueOrNull(): T? = (this as? AdminResult.Success)?.value
